import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import ApiService from '../../services/api';
import { persistAdminPortalSession } from '../../utils/adminPortalSession';
import { clearClientAuthSession } from '../../utils/clearClientAuthSession';
import { hasClientAuthSession, getClientAccessToken } from '../../utils/sessionAuth';

interface AdminRouteProps {
    children: React.ReactNode;
}

/**
 * Wrapper component that protects admin-only routes
 * Checks if user has admin role before allowing access
 */
const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
    const [checking, setChecking] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);

    useEffect(() => {
        checkAdminStatus();
    }, []);

    const checkAdminStatus = async () => {
        if (!hasClientAuthSession()) {
            setIsAdmin(false);
            setChecking(false);
            return;
        }

        try {
            const response = await ApiService.request('/auth/me');
            const user = response?.data?.user;
            const role = user?.role;
            const hasServerAuthorizedRole = role === 'admin' || role === 'view';
            const token = getClientAccessToken();

            if (hasServerAuthorizedRole && user) {
                persistAdminPortalSession(user, token);
            }

            setIsAdmin(hasServerAuthorizedRole);

            if (!hasServerAuthorizedRole) {
                localStorage.removeItem('adminUser');
            }
        } catch (error: unknown) {
            console.error('Admin session validation failed:', error);
            const status = (error as { response?: { status?: number }; status?: number })?.response?.status
                ?? (error as { status?: number })?.status;
            // Do not wipe an MSME session when an authenticated user simply lacks admin role.
            if (status === 401 || !hasClientAuthSession()) {
                clearClientAuthSession();
            }
            setIsAdmin(false);
        } finally {
            setChecking(false);
        }
    };

    if (checking) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '100vh',
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    if (!isAdmin) {
        return <Navigate to={hasClientAuthSession() ? '/dashboard' : '/login'} replace />;
    }

    return <>{children}</>;
};

export default AdminRoute;
